import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import AIJobProgressLoader, { FileAnalysisSubtitle } from 'components/ai/AIJobProgressLoader';
import JobbableAttachmentJobSubscriber from 'components/shared/ai/summary/JobbableAttachmentJobSubscriber';
import TimingExplainer from 'components/shared/ai/TimingExplainer';
import {
  summarizationLoadingStage,
  summarizationLoadingStages,
  SummarizationStage,
} from 'components/summarization/helpers';
import { useStageTimer } from 'hooks/useStageTimer';
import Summarization from 'models/Summarization';

type SummaryLoadingStateProps = {
  summarization: Summarization;
  onLoadingFinished: () => void;
};

export default function LoadingState({ summarization, onLoadingFinished }: SummaryLoadingStateProps) {
  const { attachments, patientDemographicsJob, summaryJob } = summarization;

  const fileCount = attachments.length;

  // Any write to the job's process row bumps updatedAt, so the timer keeps the first one it sees.
  const generatingSince = summaryJob?.isAwaiting || summaryJob?.isRunning ? summaryJob.updatedAt : null;
  const [timerReferenceDate, setTimerReferenceDate] = useState(generatingSince);
  if (!timerReferenceDate && generatingSince) setTimerReferenceDate(generatingSince);
  const onFinalStage = useStageTimer(timerReferenceDate, 60);

  const stageTitles = useMemo(() => summarizationLoadingStages(patientDemographicsJob), [patientDemographicsJob]);

  const stage = useMemo(
    () => summarizationLoadingStage({ stages: stageTitles, summaryJob, patientDemographicsJob, onFinalStage }),
    [stageTitles, summaryJob, patientDemographicsJob, onFinalStage]
  );

  const count = useMemo(() => {
    return stage
      ? attachments.filter(
          (attachment) => attachment.processDocumentAIJob && !attachment.processDocumentAIJob.isProcessing
        ).length
      : 0;
  }, [stage, attachments]);

  const [completedCount, setCompletedCount] = useState(count);

  useEffect(() => {
    if (stage) {
      const timer = setTimeout(() => {
        setCompletedCount(count);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [stage, count]);

  const stages = useMemo(
    () =>
      stageTitles.map((stage) => ({
        title: stage,
        ...(stage === SummarizationStage.ProcessingFiles
          ? {
              subtitle: `${completedCount} of ${fileCount} complete`,
              subtasks: [
                { title: 'Scanning document text', annotation: `${completedCount}/${fileCount}` },
                { title: 'Extracting key information', annotation: `${completedCount}/${fileCount}` },
                { title: 'Creating source citations', annotation: `${completedCount}/${fileCount}` },
              ],
            }
          : {}),
      })),
    [stageTitles, completedCount, fileCount]
  );

  return (
    <LoaderContainer>
      {attachments.map((attachment) => (
        <JobbableAttachmentJobSubscriber key={attachment.id} attachment={attachment} jobbable={summarization} />
      ))}
      <CenteredContent>
        <AIJobProgressLoader
          title='Generating Summary'
          subtitle={<FileAnalysisSubtitle fileCount={fileCount} />}
          stages={stages}
          stage={stage}
          onLoadingFinished={onLoadingFinished}
        />
      </CenteredContent>
      <TimingExplainer jobType='summary' />
    </LoaderContainer>
  );
}

const LoaderContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CenteredContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;
